import React from 'react';
import { Card, CardContent } from '../ui/card';
import { User, Building2 } from 'lucide-react';
import { tempBadge, getCandidateTemperature } from './constants';
import MissingCtcPill, { isMissingOfferedCtc } from '../common/MissingCtcPill';
import { useCanSeeRevenue } from '../../lib/perms';

/**
 * Single kanban card — extracted so both virtualized and inline lists share markup.
 */
const KanbanCard = React.memo(function KanbanCard({
  app,
  isSelected,
  isDragging,
  isHighlighted,
  onSelect,
  onDragStart,
  onDragEnd,
}) {
  const temp = getCandidateTemperature(app);
  const canSeeRevenue = useCanSeeRevenue();
  const missingCtc = canSeeRevenue && isMissingOfferedCtc(app);
  return (
    <Card
      draggable
      onDragStart={(e) => onDragStart(e, app.id)}
      onDragEnd={onDragEnd}
      onClick={() => onSelect(app.id)}
      data-testid={`cc-app-card-${app.id}`}
      title={`${app.candidate_name || 'Unknown'}${app.job_title ? ' — ' + app.job_title : ''}${app.client_name ? ' (' + app.client_name + ')' : ''}${missingCtc ? ' · No Offered CTC on file' : ''}`}
      className={`bg-white shadow-sm cursor-grab active:cursor-grabbing hover:shadow-md transition h-[72px] ${
        isSelected ? 'ring-2 ring-teal-500' : ''
      } ${
        isHighlighted ? 'ring-2 ring-yellow-400 animate-pulse cc-highlight-flash' : ''
      } ${
        missingCtc && !isSelected && !isHighlighted ? 'ring-1 ring-amber-300 bg-amber-50/40' : ''
      } ${isDragging ? 'opacity-50' : ''}`}
    >
      <CardContent className="p-2.5">
        <div className="flex items-start gap-2">
          <div className="w-7 h-7 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
            <User className="w-3.5 h-3.5 text-slate-500" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-medium text-slate-900 text-xs truncate">
              {app.candidate_name || 'Unknown'}
            </p>
            <p className="text-[11px] text-slate-500 truncate">
              {app.job_title || '—'}
            </p>
          </div>
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded border ${tempBadge(temp)}`}
            title={`${temp} candidate`}
          >
            {temp}
          </span>
        </div>
        <div className="mt-1.5 flex items-center gap-1 text-[11px] text-slate-500 truncate">
          <Building2 className="w-3 h-3 flex-shrink-0" />
          <span className="truncate flex-1">{app.client_name || '—'}</span>
          {missingCtc && (
            <MissingCtcPill
              size="xs"
              data-testid={`cc-missing-ctc-${app.id}`}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
});

export default KanbanCard;
